import React, { useEffect, useState } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { TextField, InputAdornment, Divider, CircularProgress } from "@mui/material";
import Icon from "@mui/material/Icon";
import { useMaterialUIController } from "context";
import { call } from "services/axiosService/core";
import * as Sentry from "@sentry/browser";
import { useTranslation } from "react-i18next";
import { setProfile } from "context";
import palette from "assets/theme/base/palette";

export default function Coupon({
  setAmountToProcess,
  setProceedtoPayment,
  childDetails,
  handleClosePaymentModal,
  setSessionExpired,
  setCouponUsed,
  subscriptionType,
}) {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { profile } = controller;
  const [referralCode, setReferralCode] = useState("");
  const [codeApplied, setCodeApplied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [helperText, setHelperText] = useState("");
  const [showHelperText, setShowHelperText] = useState(false);
  const [amount, setAmount] = useState(null);
  const [paymentNotRequired, setPaymentNotRequired] = useState(false);
  const [demoUser, setDemoUser] = useState(false);

  useEffect(() => {
    const referral = localStorage.getItem("referral");
    if (referral) {
      setReferralCode(referral);
      verifyCouponHandler(referral);
    } else {
      const renewalCode = childDetails.referral_code || null;
      if (renewalCode && renewalCode.length > 0) {
        setReferralCode(renewalCode);
        verifyCouponHandler(renewalCode);
      }
    }
  }, []);

  const verifyCouponHandler = (code) => {
    setIsLoading(true);
    const data = {
      code: code,
      subscription_type: subscriptionType,
      learner_email: childDetails.email,
    };
    call(`payments/verify-coupon`, "POST", { body: data })
      .then((response) => {
        if (response.status == 200) {
          if (parseFloat(response.data.amount_to_process) > 0) {
            setHelperText(t("alerts.referrals.couponApplied", { amount: response.data.amount_to_process }));
          } else {
            if (response.data.is_demo_user) {
              setDemoUser(true);
              setHelperText(t("alerts.referrals.noPaymentRequired"));
            } else {
              setPaymentNotRequired(true);
              setHelperText(t("alerts.referrals.noPaymentRequired"));
            }
          }
          setCodeApplied(true);
          setShowHelperText(true);
          setAmount(response.data.amount_to_process);
          setCouponUsed(code);
        } else if (response.status == 404 || response.status == 403) {
          setHelperText(t("alerts.referrals.notValid"));
          setShowHelperText(true);
          setAmount(null);
          setCouponUsed(null);
        }
        setIsLoading(false);
      })
      .catch(function (error) {
        Sentry.captureException(error);
        setIsLoading(false);
        setAmount(null);
        setCouponUsed(null);
        setHelperText(t("alerts.referrals.error"));
        setShowHelperText(true);
      });

    setIsLoading(true);
  };

  const RemoveCouponHandler = () => {
    setPaymentNotRequired(false);
    setCodeApplied(false);
    setDemoUser(false);
    if (subscriptionType == "true_mindsets_monthly") {
      setAmount(45);
    } else if (subscriptionType == "true_mindsets_yearly") {
      setAmount(480);
    } else {
      setAmount(99);
    }
    setShowHelperText(false);
    setPaymentNotRequired(false);
    setHelperText(t("alerts.referrals.removeCoupon"));
    setCouponUsed(null);
  };

  const proceedToPaymentHandler = () => {
    if (demoUser || paymentNotRequired) {
      let LearnerDetails = childDetails;
      const source =
        LearnerDetails.source ||
        (referralCode && referralCode.includes("_") ? `${referralCode.split("_")[0]}_code` : null);
      LearnerDetails = {
        ...LearnerDetails,
        source: source,
        payment_status: "success",
        paid_at: new Date(),
        is_demo_user: demoUser,
        referral_code: referralCode,
        subscription_type: subscriptionType,
      };
      call(`payments/apply-coupon`, "PUT", {
        body: { email: LearnerDetails.email, code: referralCode },
      })
        .then(() => {})
        .catch(function (error) { Sentry.captureException(error); });

      call(`users/${LearnerDetails.id}`, "PUT", {
        body: LearnerDetails,
        headers: { "Content-Type": "application/json" },
      })
        .then((response) => {
          if (response.status == 200) {
            const shouldAdd = !demoUser;
            handleClosePaymentModal("PaymentNotRequired", shouldAdd);
          }
        })
        .catch(function (error) { Sentry.captureException(error); });

      if (LearnerDetails.id != profile.id && profile.is_demo_user == false && demoUser == true) {
        profile.is_demo_user = true;
        call(`users/${profile.id}`, "PUT", {
          body: profile,
          headers: { "Content-Type": "application/json" },
        })
          .then((response) => {
            if (response.status == 200) setProfile(profile);
          })
          .catch(function (error) { Sentry.captureException(error); });
      }
    } else {
      if (amount) setAmountToProcess(amount);
      setProceedtoPayment(true);
    }
  };

  const isCodeValid = codeApplied && showHelperText;
  const isCodeInvalid = !codeApplied && showHelperText;

  return (
    <MDBox>
      {/* Header */}
      <MDBox textAlign="center" mb={3}>
        <MDBox
          sx={{
            width: 64, height: 64, borderRadius: "50%",
            bgcolor: `${palette.brand[500]}18`,
            display: "flex", alignItems: "center", justifyContent: "center",
            mx: "auto", mb: 1.5,
          }}
        >
          <Icon sx={{ fontSize: "32px !important", color: palette.brand[500] }}>card_giftcard</Icon>
        </MDBox>
        <MDTypography variant="h6" fontWeight="bold">
          {t("formLabels.haveReferral")}
        </MDTypography>
      </MDBox>

      {/* Input field */}
      <TextField
        fullWidth
        size="small"
        placeholder={t("formPlaceHolders.haveReferral")}
        value={referralCode}
        disabled={codeApplied}
        onChange={(e) => {
          if (e.nativeEvent.data !== " ") setReferralCode(e.target.value.trim());
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon
                sx={{
                  fontSize: "18px !important",
                  color: isCodeValid ? "#4caf50" : isCodeInvalid ? "#f44336" : palette.neutral[400],
                }}
              >
                {isCodeValid ? "check_circle" : "sell"}
              </Icon>
            </InputAdornment>
          ),
          sx: {
            borderRadius: "12px",
            bgcolor: isCodeValid ? "#f1f8e9" : "#fafafa",
            "& fieldset": {
              borderColor: isCodeValid ? "#4caf50 !important" : isCodeInvalid ? "#f44336 !important" : undefined,
            },
          },
        }}
        sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
      />

      {/* Feedback message */}
      {showHelperText && (
        <MDBox
          display="flex"
          alignItems="center"
          gap={1}
          sx={{
            px: 1.5, py: 1.25, borderRadius: "12px", mb: 2,
            bgcolor: codeApplied ? "#f1f8e9" : "#fdecea",
            border: `1px solid ${codeApplied ? "#a5d6a7" : "#ef9a9a"}`,
          }}
        >
          <Icon sx={{ fontSize: "18px !important", color: codeApplied ? "#4caf50" : "#f44336", flexShrink: 0 }}>
            {codeApplied ? "check_circle" : "error_outline"}
          </Icon>
          <MDTypography variant="body2" fontWeight="medium" sx={{ color: codeApplied ? "#2e7d32" : "#c62828" }}>
            {helperText}
          </MDTypography>
        </MDBox>
      )}

      {/* Apply / Remove button */}
      <MDBox mb={3}>
        {codeApplied ? (
          <MDButton
            variant="outlined"
            fullWidth
            onClick={RemoveCouponHandler}
            sx={{
              borderRadius: "12px", py: 1.25, textTransform: "none", fontWeight: "bold",
              borderColor: "#f44336", color: "#f44336",
              "&:hover": { bgcolor: "#fdecea", borderColor: "#d32f2f" },
            }}
          >
            <Icon sx={{ mr: 0.75, fontSize: "18px !important" }}>remove_circle_outline</Icon>
            {t("buttonTitles.removeReferralCode")}
          </MDButton>
        ) : (
          <MDButton
            variant="contained"
            fullWidth
            onClick={() => verifyCouponHandler(referralCode)}
            disabled={referralCode.length < 1 || isLoading}
            sx={{
              borderRadius: "12px", py: 1.25, textTransform: "none", fontWeight: "bold",
              bgcolor: palette.brand[500], color: "#fff",
              "&:hover": { bgcolor: palette.brand[600] },
              "&.Mui-disabled": { bgcolor: palette.neutral[200], color: palette.neutral[500] },
            }}
          >
            {isLoading
              ? <CircularProgress size={20} color="inherit" />
              : <><Icon sx={{ mr: 0.75, fontSize: "18px !important" }}>verified</Icon>{t("buttonTitles.applyReferralCode")}</>
            }
          </MDButton>
        )}
      </MDBox>

      <Divider sx={{ mb: 3 }} />

      {/* Proceed button */}
      <MDButton
        variant="contained"
        fullWidth
        onClick={proceedToPaymentHandler}
        disabled={isLoading}
        sx={{
          borderRadius: "12px", py: 1.5, textTransform: "none", fontWeight: "bold",
          bgcolor: palette.brand[500], color: "#fff",
          "&:hover": { bgcolor: palette.brand[600] },
          "&.Mui-disabled": { bgcolor: palette.neutral[300], color: "#fff" },
        }}
      >
        <Icon sx={{ mr: 0.75, fontSize: "18px !important" }}>
          {paymentNotRequired || demoUser ? "check_circle" : "payment"}
        </Icon>
        {paymentNotRequired || demoUser
          ? t("buttonTitles.proceedToFinish")
          : t("buttonTitles.proceedToPayment")}
      </MDButton>
    </MDBox>
  );
}
